import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/client';
import type { Paged } from '@core/api/masters';
import {
  vendorLedgerPath,
  vendorPaymentAccountsPath,
  vendorPaymentPath,
  vendorPaymentsPath,
} from '@core/api/queryPaths';
import type { CashAccountChoice } from '@core/api/cash';
import type {
  VendorLedger,
  VendorPayment,
  VendorPaymentFilters,
  VendorPaymentSummary,
} from '@core/api/vendorPayments';

export type {
  VendorDue,
  VendorLedger,
  VendorPayment,
  VendorPaymentSummary,
} from '@core/api/vendorPayments';

export const vendorPaymentsKey = ['vendor-payment'] as const;

export function useVendorPayments(
  branchId: number | undefined,
  filters: VendorPaymentFilters = {},
) {
  return useQuery({
    queryKey: [...vendorPaymentsKey, { branchId, ...filters }],
    queryFn: () => apiFetch<Paged<VendorPaymentSummary>>(vendorPaymentsPath(filters)),
    enabled: branchId !== undefined,
    placeholderData: (previous) => previous,
  });
}

export function useVendorPayment(id: number | null) {
  return useQuery({
    queryKey: [...vendorPaymentsKey, 'one', id],
    queryFn: () => apiFetch<VendorPayment>(vendorPaymentPath(id ?? 0)),
    enabled: id !== null,
  });
}

/** What a vendor is owed at the acting branch, oldest first. */
export function useVendorLedger(vendorId: number | null) {
  return useQuery({
    queryKey: [...vendorPaymentsKey, 'ledger', vendorId],
    queryFn: () => apiFetch<VendorLedger>(vendorLedgerPath(vendorId ?? 0)),
    enabled: vendorId !== null,
  });
}

/** What a payment can be paid from — under the cash key, as the expense picker's is. */
export function useVendorPaymentAccounts(branchId: number | undefined) {
  return useQuery({
    queryKey: ['cash', 'paid-from', 'vendor-payment', { branchId }],
    queryFn: () => apiFetch<CashAccountChoice[]>(vendorPaymentAccountsPath()),
    enabled: branchId !== undefined,
  });
}

export const cancelVendorPayment = (id: number, reason: string) =>
  apiFetch<VendorPayment>(`/api/vendor-payment/${id}/cancel`, { method: 'POST', body: { reason } });

export { createVendorPayment } from '@core/api/purchaseCommands';
